import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Shield, FileText, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { APP_NAME } from "../config/branding";

const TrustSplash = () => {
  const [show, setShow] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has seen the trust splash
    const seen = localStorage.getItem("trustSplashSeen");
    if (!seen) {
      // Show after a small delay for better UX
      const timer = setTimeout(() => setShow(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleDismiss = () => {
    localStorage.setItem("trustSplashSeen", "true");
    setShow(false);
  };

  const handleUpload = () => {
    handleDismiss();
    navigate("/upload");
  };

  const handleAsk = () => {
    handleDismiss();
    navigate("/ask");
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-lg mx-4 p-6 animate-in fade-in zoom-in-95">
        <div className="text-center mb-6">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Shield className="w-8 h-8 text-primary" />
          </div>
          <h2 className="text-2xl font-heading font-semibold mb-2">
            Welcome to {APP_NAME}
          </h2>
          <p className="text-muted-foreground">
            Plain-English answers to complex workplace agreements
          </p>
        </div>

        <div className="space-y-4 mb-6">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Shield className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Privacy-first</p>
              <p className="text-sm text-muted-foreground">
                Your documents stay private and are never shared
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <FileText className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Grounded answers</p>
              <p className="text-sm text-muted-foreground">
                Every response includes exact contract citations
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircle className="w-4 h-4 text-primary" />
            </div>
            <div>
              <p className="text-sm font-medium mb-1">Use ethically</p>
              <p className="text-sm text-muted-foreground">
                Compliance and fairness first - verify before acting
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={handleUpload} className="flex-1">
            <FileText className="w-4 h-4 mr-2" />
            Upload a CBA
          </Button>
          <Button onClick={handleAsk} variant="outline" className="flex-1">
            Ask a Question
          </Button>
        </div>

        <button
          onClick={handleDismiss}
          className="text-sm text-muted-foreground hover:text-foreground mt-4 w-full text-center"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default TrustSplash;
